import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import CompleteProfilePage from "@/pages/CompleteProfilePage";
import VerificationPending from "@/pages/VerificationPending";
import BrokerDashboard from "@/pages/BrokerDashboard";
import BillingPage from "@/pages/BillingPage";
import MockCheckoutPage from "@/pages/MockCheckoutPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import BillingCancelledPage from "@/pages/BillingCancelledPage";
import AdminLayout from "@/pages/AdminLayout";
import AdminIndexRouter from "@/pages/AdminIndexRouter";
import AdminQueue from "@/pages/AdminQueue";
import AdminDisputesPage from "@/pages/AdminDisputesPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminBrokerDirectoryPage from "@/pages/AdminBrokerDirectoryPage";
import AdminFlaggedPage from "@/pages/AdminFlaggedPage";
import AdminBroadcastPage from "@/pages/AdminBroadcastPage";
import AdminExportsPage from "@/pages/AdminExportsPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import AdminForgotPage from "@/pages/AdminForgotPage";
import AdminResetPage from "@/pages/AdminResetPage";
import ControlPanelLoginPage from "@/pages/ControlPanelLoginPage";
import { GlobalBanner } from "@/components/GlobalBanner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminRole } from "@/lib/utils";

// Obscured, permanent admin base path. Emergent Support confirmed
// (2026-02-26) that the platform allows exactly one custom domain per
// deployment — a dedicated admin.<host> subdomain can never bind at
// the origin. Admin access on production is served via a Cloudflare
// 301 redirect from admin.matchbroker.ph → matchbroker.ph{PATH} on
// this obscured route. The path must never be linked from the public UI.
export const CONTROL_PANEL_PATH = "/control-panel-x9";

function AuthGuard({ children }) {
  // If the user is already authenticated, block re-entry into login/register.
  const { user, status } = useAuth();
  if (status === "loading") {
    return (
      <div className="mb-page flex min-h-screen items-center justify-center">
        <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-[#1B2B4B] animate-spin" />
      </div>
    );
  }
  if (status === "authenticated" && user) {
    if (isAdminRole(user.role)) return <Navigate to={CONTROL_PANEL_PATH} replace />;
    if (user.profile_incomplete) return <Navigate to="/complete-profile" replace />;
    if (!user.is_verified) return <Navigate to="/verification-pending" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Gate for the obscured control-panel login page — if already signed in
// as admin, jump straight into the panel. Otherwise let them see the page.
function ControlPanelAuthGuard({ children }) {
  const { user, status } = useAuth();
  if (status === "loading") {
    return (
      <div className="mb-page flex min-h-screen items-center justify-center">
        <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-[#1B2B4B] animate-spin" />
      </div>
    );
  }
  if (status === "authenticated" && isAdminRole(user?.role)) {
    return <Navigate to={CONTROL_PANEL_PATH} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <GlobalBanner />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route
              path="/login"
              element={
                <AuthGuard>
                  <LoginPage />
                </AuthGuard>
              }
            />
            <Route
              path="/register"
              element={
                <AuthGuard>
                  <RegisterPage />
                </AuthGuard>
              }
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Public legal pages — no auth guard. Required for Google
                OAuth consent screen review + PH RA 10173 disclosure. */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route
              path="/verification-pending"
              element={
                <ProtectedRoute allowUnverified>
                  <VerificationPending />
                </ProtectedRoute>
              }
            />
            <Route
              path="/complete-profile"
              element={
                <ProtectedRoute allowUnverified allowProfileIncomplete>
                  <CompleteProfilePage />
                </ProtectedRoute>
              }
            />
            <Route path="/auth/google-callback" element={<AuthCallbackPage />} />
            <Route path="/mock-checkout/:checkoutId" element={<MockCheckoutPage />} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="/billing/cancelled" element={<BillingCancelledPage />} />
            <Route
              path="/dashboard/*"
              element={
                <ProtectedRoute>
                  <BrokerDashboard />
                </ProtectedRoute>
              }
            />

            {/* Obscured, permanent admin surface. */}
            <Route
              path={`${CONTROL_PANEL_PATH}/login`}
              element={
                <ControlPanelAuthGuard>
                  <ControlPanelLoginPage />
                </ControlPanelAuthGuard>
              }
            />
            <Route
              path={`${CONTROL_PANEL_PATH}/forgot`}
              element={<AdminForgotPage />}
            />
            <Route
              path={`${CONTROL_PANEL_PATH}/reset`}
              element={<AdminResetPage />}
            />
            <Route
              path={`${CONTROL_PANEL_PATH}/*`}
              element={
                <ProtectedRoute adminOnly>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminIndexRouter />} />
              <Route path="queue" element={<AdminQueue />} />
              <Route path="users" element={<AdminBrokerDirectoryPage />} />
              <Route path="admins" element={<AdminUsersPage />} />
              <Route path="flagged" element={<AdminFlaggedPage />} />
              <Route path="broadcast" element={<AdminBroadcastPage />} />
              <Route path="exports" element={<AdminExportsPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
              <Route path="disputes" element={<AdminDisputesPage />} />
              <Route path="*" element={<Navigate to={CONTROL_PANEL_PATH} replace />} />
            </Route>

            {/* Any unknown path (including former /admin*) → landing. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
